import { useState, useEffect } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, CalendarCheck2, Instagram } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import ExpressPopup from "@/components/ExpressPopup";
import LiveOrderFeed from "@/components/LiveOrderFeed";
import CustomCursor from "@/components/CustomCursor";
import { INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/constants";
import { STATIONERY_NAV } from "@/lib/stationeryNav";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "Atelier" },
  { to: "/services", label: "Wedding", dropdown: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/express-delhi-ncr", label: "Express NCR" },
  { to: "/book", label: "Book Meeting" },
  { to: "/career", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-[40px] inset-x-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#0E0709]/85 backdrop-blur-xl border-b border-[#3A2530]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-16 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-3 group">
          <span className="font-heading text-2xl md:text-[28px] tracking-tight text-[#F8EDE7] group-hover:text-[#F0C4BC] transition-colors">
            Stationery <span className="italic-serif">Wala</span>
          </span>
          <span className="hidden md:inline overline text-[#C4A8B0]">est.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {navItems.map((item) =>
            item.dropdown ? (
              <div key={item.to} className="relative group">
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `relative text-[13px] tracking-[0.05em] uppercase transition-colors pb-1 ${
                      isActive ? "text-[#F0C4BC]" : "text-[#F8EDE7] hover:text-[#F0C4BC]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute -bottom-1 left-0 h-px bg-[#F0C4BC] transition-all duration-500 ${
                          isActive ? "w-full" : "w-0"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
                <div
                  data-testid="nav-wedding-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-5 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50"
                >
                  <div className="w-72 bg-[#0E0709]/95 backdrop-blur-xl border border-[#3A2530] py-3 shadow-2xl">
                    {STATIONERY_NAV.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        data-testid={`nav-dd-${s.slug}`}
                        className="block px-6 py-2.5 text-[12px] tracking-[0.08em] uppercase text-[#F8EDE7] hover:text-[#F0C4BC] hover:bg-[#1F1218] transition-colors"
                      >
                        {s.label}
                      </Link>
                    ))}
                    <div className="mx-6 my-2 h-px bg-[#3A2530]" />
                    <Link
                      to="/services"
                      data-testid="nav-dd-view-all"
                      className="block px-6 py-2.5 text-[12px] tracking-[0.08em] uppercase text-[#F0C4BC] hover:bg-[#1F1218] transition-colors"
                    >
                      View All Wedding Stationery →
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative text-[13px] tracking-[0.05em] uppercase transition-colors pb-1 ${
                    isActive ? "text-[#F0C4BC]" : "text-[#F8EDE7] hover:text-[#F0C4BC]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute -bottom-1 left-0 h-px bg-[#F0C4BC] transition-all duration-500 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            aria-label="Instagram"
            className="w-11 h-11 border border-[#3A2530] hover:border-[#F0C4BC] hover:bg-[#1F1218] flex items-center justify-center text-[#F8EDE7] hover:text-[#F0C4BC] transition-colors"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <Link
            to="/book"
            data-testid="header-book-cta"
            className="btn-luxe bg-[#F0C4BC] text-[#0E0709] px-6 py-3.5 text-[12px] tracking-[0.18em] uppercase inline-flex items-center gap-2"
          >
            <CalendarCheck2 className="w-4 h-4" />
            Book a Meeting
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-[#F8EDE7]"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0E0709] border-t border-[#3A2530]" data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-5">
            {navItems.map((item) => (
              <div key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-heading text-2xl ${isActive ? "text-[#F0C4BC]" : "text-[#F8EDE7]"}`
                  }
                >
                  {item.label}
                </NavLink>
                {item.dropdown && (
                  <div className="mt-3 ml-4 pl-4 border-l border-[#3A2530] flex flex-col gap-2.5" data-testid="mobile-stationery-links">
                    {STATIONERY_NAV.map((s) => (
                      <NavLink
                        key={s.slug}
                        to={`/${s.slug}`}
                        data-testid={`mobile-nav-dd-${s.slug}`}
                        className={({ isActive }) =>
                          `text-[12px] tracking-[0.1em] uppercase ${isActive ? "text-[#F0C4BC]" : "text-[#C4A8B0] hover:text-[#F0C4BC]"}`
                        }
                      >
                        {s.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/book"
              data-testid="mobile-book-cta"
              className="bg-[#F0C4BC] text-[#0E0709] px-6 py-4 text-sm tracking-[0.18em] uppercase text-center mt-2"
            >
              Book a Meeting
            </Link>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-instagram"
              className="border border-[#3A2530] text-[#F8EDE7] px-6 py-4 text-sm tracking-[0.18em] uppercase text-center inline-flex items-center justify-center gap-2 hover:border-[#F0C4BC] hover:text-[#F0C4BC]"
            >
              <Instagram className="w-4 h-4" /> @{INSTAGRAM_HANDLE}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default function Layout() {
  return (
    <div className="min-h-screen bg-[#0E0709] text-[#F8EDE7] flex flex-col relative">
      <AnnouncementBar />
      <Header />
      <main className="flex-1 pt-[100px] md:pt-[110px] relative z-[2]">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppButton />
      <ExpressPopup />
      <LiveOrderFeed />
    </div>
  );
}
